import React, { useState } from 'react';
import { Lock, Unlock, Compass, CheckCircle, Users, Shield, ChevronRight } from 'lucide-react';
import BrandName from './BrandName';
import PublicDesignerApplication from './PublicDesignerApplication';

const ComingSoon = ({ onAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDesignerApplication, setShowDesignerApplication] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Get password from environment variable
    const sitePassword = process.env.REACT_APP_SITE_PASSWORD || 'Create543687123!32';

    // Check password
    if (password === sitePassword) {
      // Store authentication in localStorage
      localStorage.setItem('site_authenticated', 'true');
      setTimeout(() => {
        onAuthenticated();
      }, 500);
    } else {
      setError('Incorrect password. Please try again.');
      setIsLoading(false);
      setPassword('');
    }
  };

  // Show designer application if user clicked the button
  if (showDesignerApplication) {
    return <PublicDesignerApplication onBack={() => setShowDesignerApplication(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Logo and Brand */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo-nav.png" 
              alt="Create 3D Works Logo" 
              className="h-24 w-auto"
            />
          </div>
          <p className="text-slate-700 text-xl font-medium">Professional 3D Printing Services</p>
        </div>

        {/* Coming Soon Card */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-slate-200">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
              <Lock className="w-10 h-10 text-[#0007b5]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Coming Soon
            </h2>
            <p className="text-slate-700 text-lg mb-2">
              We're preparing something amazing!
            </p>
            <p className="text-slate-600 text-base">
              Our new website is under construction and will be launching soon.
            </p>
          </div>

          {/* Password Access */}
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Unlock className="w-5 h-5 text-[#0007b5]" />
              <p className="text-slate-700 text-sm font-medium">Have access? Enter password to preview</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0007b5] focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                  <p className="text-red-700 text-sm text-center">{error}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isLoading || !password}
                className="w-full bg-[#0007b5] hover:bg-[#000690] disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <Unlock className="w-5 h-5" />
                    <span>Access Site</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-slate-600 text-sm mb-2">Need access or have questions?</p>
            <a 
              href="mailto:hello@create3dworks.com" 
              className="text-[#0007b5] hover:text-[#000690] font-medium transition-colors"
            >
              hello@create3dworks.com
            </a>
          </div>
        </div>

        {/* Professional Designer Invitation Card */}
        <div className="mt-8 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl p-6 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                <Compass className="w-6 h-6 text-[#0007b5]" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Professional 3D Designers Welcome
              </h3>
              <p className="text-sm text-slate-700 mb-4">
                Want to learn more about joining our platform? Request more information about generating revenue from your designs while maintaining full IP.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Passive Income
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                  <Users className="w-3 h-3 mr-1" />
                  Global Reach
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                  <Shield className="w-3 h-3 mr-1" />
                  IP Protected
                </span>
              </div>
              <button
                onClick={() => setShowDesignerApplication(true)}
                className="inline-flex items-center px-4 py-2 bg-[#0007b5] hover:bg-[#000690] text-white text-sm font-medium rounded-lg transition-colors"
              >
                Request Information
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} CREATE 3D Works. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
